export interface MockKeyword {
  keyword: string;
  bidEstimate: number;
  searchVolume: number;
  competition: "Low" | "Medium" | "High";
  affordable: boolean;
}

export interface AdGroup {
  name: string;
  icon: string;
  keywords: string[];
  avgCPC: number;
  estImpressions: number;
  estClicks: number;
}

export const mockBudget = {
  dailyBudget: 50,
  targetCPA: 15,
};

export const allKeywords: MockKeyword[] = [
  // Affordable keywords (bid < CPA)
  { keyword: "roof repair near me", bidEstimate: 8.50, searchVolume: 12100, competition: "Medium", affordable: true },
  { keyword: "emergency roof leak", bidEstimate: 11.20, searchVolume: 8800, competition: "Medium", affordable: true },
  { keyword: "roof inspection cost", bidEstimate: 4.30, searchVolume: 6600, competition: "Low", affordable: true },
  { keyword: "flat roof repair", bidEstimate: 6.80, searchVolume: 5400, competition: "Low", affordable: true },
  { keyword: "roof damage assessment", bidEstimate: 3.90, searchVolume: 4400, competition: "Low", affordable: true },
  { keyword: "storm damage roof", bidEstimate: 9.10, searchVolume: 9900, competition: "Medium", affordable: true },
  { keyword: "shingle replacement", bidEstimate: 5.60, searchVolume: 7200, competition: "Low", affordable: true },
  { keyword: "roof patching service", bidEstimate: 4.10, searchVolume: 3800, competition: "Low", affordable: true },
  { keyword: "gutter repair roofing", bidEstimate: 3.20, searchVolume: 5100, competition: "Low", affordable: true },
  { keyword: "residential roof fix", bidEstimate: 7.40, searchVolume: 4900, competition: "Medium", affordable: true },
  { keyword: "leaking roof solutions", bidEstimate: 6.20, searchVolume: 6100, competition: "Low", affordable: true },
  { keyword: "roof maintenance plan", bidEstimate: 3.50, searchVolume: 3200, competition: "Low", affordable: true },
  { keyword: "tile roof repair", bidEstimate: 8.90, searchVolume: 4100, competition: "Medium", affordable: true },
  { keyword: "metal roof fix", bidEstimate: 7.10, searchVolume: 3600, competition: "Low", affordable: true },
  { keyword: "affordable roof repair", bidEstimate: 5.80, searchVolume: 8200, competition: "Medium", affordable: true },
  { keyword: "local roofer quote", bidEstimate: 6.90, searchVolume: 7800, competition: "Medium", affordable: true },
  { keyword: "roof sealant service", bidEstimate: 2.80, searchVolume: 2900, competition: "Low", affordable: true },
  { keyword: "chimney flashing repair", bidEstimate: 4.60, searchVolume: 2400, competition: "Low", affordable: true },
  // Expensive keywords (bid > CPA)
  { keyword: "roofing contractor", bidEstimate: 42.50, searchVolume: 33100, competition: "High", affordable: false },
  { keyword: "best roofer", bidEstimate: 38.20, searchVolume: 27400, competition: "High", affordable: false },
  { keyword: "roof replacement", bidEstimate: 35.80, searchVolume: 40500, competition: "High", affordable: false },
  { keyword: "new roof cost", bidEstimate: 28.60, searchVolume: 22000, competition: "High", affordable: false },
  { keyword: "commercial roofing", bidEstimate: 52.30, searchVolume: 18200, competition: "High", affordable: false },
  { keyword: "roofing company", bidEstimate: 45.10, searchVolume: 49500, competition: "High", affordable: false },
  { keyword: "roof installation", bidEstimate: 31.40, searchVolume: 14800, competition: "High", affordable: false },
  { keyword: "roofing services", bidEstimate: 39.70, searchVolume: 27100, competition: "High", affordable: false },
];

export const affordableKeywords = allKeywords.filter((k) => k.affordable);
export const expensiveKeywords = allKeywords.filter((k) => !k.affordable);

export const adGroups: AdGroup[] = [
  {
    name: "Emergency Repairs",
    icon: "Zap",
    keywords: ["emergency roof leak", "storm damage roof", "leaking roof solutions", "roof damage assessment"],
    avgCPC: 8.55,
    estImpressions: 2400,
    estClicks: 168,
  },
  {
    name: "Affordable Solutions",
    icon: "DollarSign",
    keywords: ["affordable roof repair", "roof inspection cost", "local roofer quote", "roof patching service"],
    avgCPC: 5.25,
    estImpressions: 2100,
    estClicks: 189,
  },
  {
    name: "Specific Repairs",
    icon: "Wrench",
    keywords: ["shingle replacement", "tile roof repair", "metal roof fix", "chimney flashing repair", "flat roof repair"],
    avgCPC: 6.60,
    estImpressions: 1700,
    estClicks: 119,
  },
  {
    name: "Maintenance & Prevention",
    icon: "Shield",
    keywords: ["roof maintenance plan", "roof sealant service", "gutter repair roofing"],
    avgCPC: 3.17,
    estImpressions: 1100,
    estClicks: 99,
  },
  {
    name: "Residential Services",
    icon: "Home",
    keywords: ["roof repair near me", "residential roof fix", "local roofer quote"],
    avgCPC: 7.10,
    estImpressions: 2500,
    estClicks: 175,
  },
];

export const impactStats = [
  { label: "More Keywords Active", value: 340, suffix: "%", prefix: "+" },
  { label: "Less Wasted Spend", value: 62, suffix: "%", prefix: "-" },
  { label: "Higher Ad Relevance", value: 85, suffix: "%", prefix: "+" },
  { label: "More Daily Impressions", value: 3.2, suffix: "x", prefix: "" },
];

export const comparisonData = {
  without: [
    { metric: "Active keywords", value: "2" },
    { metric: "Avg. CPC", value: "$42.50" },
    { metric: "Budget lasts until", value: "10:15 AM" },
    { metric: "Daily clicks", value: "1-2" },
    { metric: "Ad relevance", value: "Low" },
    { metric: "Quality Score", value: "3/10" },
  ],
  with: [
    { metric: "Active keywords", value: "89+" },
    { metric: "Avg. CPC", value: "$5.80" },
    { metric: "Budget lasts until", value: "11:59 PM" },
    { metric: "Daily clicks", value: "45+" },
    { metric: "Ad relevance", value: "Excellent" },
    { metric: "Quality Score", value: "8/10" },
  ],
};

export const faqItems = [
  {
    question: "How does budget-based keyword filtering work?",
    answer: "Upfixe compares your daily budget and target CPA against the estimated bid for each keyword from Google's Keyword Planner data. Keywords where the bid estimate exceeds your target CPA are automatically filtered out, ensuring you only bid on keywords you can realistically convert on within your budget constraints. This prevents the common mistake of spending your entire daily budget on just one or two clicks from expensive, competitive keywords."
  },
  {
    question: "What if my daily budget is very small?",
    answer: "Budget-based filtering actually becomes more valuable with smaller budgets. If you're spending $20-50/day, every click matters. Upfixe ensures your budget is spread across multiple affordable keywords rather than exhausted on a single expensive one. Even with a $20 daily budget, you can typically target 15-30 relevant keywords with CPCs under your target CPA."
  },
  {
    question: "How are thematic ad groups created?",
    answer: "After filtering and sorting keywords by affordability and search volume, Upfixe's AI analyses your value proposition document and landing page content to group keywords into 5 thematic clusters. Each group shares a common intent or topic, which allows Google to show more relevant ads and improves your Quality Score. Higher Quality Scores mean lower actual CPCs and better ad positions."
  },
  {
    question: "Does this work for all industries?",
    answer: "Yes. The budget-to-bid comparison works universally because it's based on your specific financial constraints, not industry benchmarks. Whether you're in legal services (high CPCs) or local retail (low CPCs), Upfixe filters keywords relative to what you can afford. The thematic grouping adapts to any industry through your value proposition and landing page analysis."
  },
  {
    question: "How many keywords does Upfixe typically find?",
    answer: "Upfixe's multi-source keyword pipeline generates 150+ keyword candidates from your website content, seed keyword expansion, and landing page analysis. After budget-based filtering, most campaigns retain 60-100 affordable, high-volume keywords organized into 5 thematic ad groups."
  },
  {
    question: "Why is thematic grouping better than one big keyword list?",
    answer: "Google rewards ad relevance. When keywords in an ad group share a theme, your ads can be more specific to the searcher's intent, which increases click-through rates and Quality Score. A higher Quality Score directly lowers your cost-per-click, meaning you get more clicks for the same budget. One large keyword list forces generic ad copy that performs poorly."
  },
  {
    question: "How often are keywords re-evaluated?",
    answer: "Keyword performance data is refreshed with every campaign update. As bid estimates change in your market, Upfixe automatically adjusts which keywords pass the affordability filter. Keywords that become too expensive are paused, and newly affordable keywords are activated, keeping your campaigns optimised without manual monitoring."
  },
  {
    question: "Can I override the keyword filtering?",
    answer: "The automated filtering is designed to maximise your budget efficiency, but Upfixe gives you full visibility into which keywords were filtered and why. You can review the complete keyword list including filtered keywords, their bid estimates, and search volumes. The system is transparent about every decision it makes."
  },
];
